import styled, { css } from "styled-components"

import Link from "~/components/atoms/Link"
import Label from "~/components/atoms/Tag"
import Typography from "~/components/atoms/Typography"
import FavoriteButton from "~/components/molecules/FavoriteButton"
import User from "~/components/organisms/User"
import { RecruitmentStatus, RecruitmentStatusJp, RecruitmentType } from "~/consts"
import { Color, RadiusSize, TextSize, Transition } from "~/consts/style-mixins"
import { Event } from "~/entities/Event"
import { Magazine } from "~/entities/Magazine"
import { NewGradJob } from "~/entities/NewGradJob"
import { ShortInternship } from "~/entities/ShortInternship"
import { internalLink } from "~/services/helper"

interface Props {
  recruitment: NewGradJob | ShortInternship | Event | Magazine
  category: keyof typeof RecruitmentType
  className?: string
  isFavorited?: boolean
  onClickFavorite?: any
}

interface StyledProps {
  bgSrc?: string
}

const RecruitmentCard = ({ recruitment, category, isFavorited, onClickFavorite, className }: Props) => {
  return (
    <StyledRecruitmentCardWrapper className={className}>
      <StyledRecruitmentLinkArea>
        <StyledThumbnailImageWrapper>
          {/* NOTE: 新しいnext.jsではdynamicRoutePath(NextLink#as)がいらない */}
          <Link href={recruitment.url}>
            <StyledThumbnailImage bgSrc={recruitment.thumbnailImgUrl} className="recruitment-img" />
          </Link>
          {recruitment.status === RecruitmentStatus.private && (
            <StyledLabel size="Medium" color={Color.Attention}>
              {RecruitmentStatusJp[recruitment.status]}
            </StyledLabel>
          )}
          {isFavorited !== undefined && (
            <StyledFavoriteButton
              isFavorited={Boolean(isFavorited)}
              onClick={() => onClickFavorite && onClickFavorite()}
              size="Medium"
            />
          )}
        </StyledThumbnailImageWrapper>
        <Link href={recruitment.url}>
          <StyledTagWrapper>
            <StyledCategory>{RecruitmentType[category]}</StyledCategory>
            {(category === "newGradJob" || category === "shortInternship" || category === "longInternship") && (
              // OPTIMIZE: instance ofで判定すると、フロントエンドに表示されなくなるので仕方なく
              // @ts-expect-error
              <StyledJobType>{recruitment.jobType}</StyledJobType>
            )}
          </StyledTagWrapper>
          <StyledTitle type="SubHeading">{recruitment.title}</StyledTitle>
        </Link>
      </StyledRecruitmentLinkArea>
      <Link href={internalLink(`/companies/${recruitment.company.id}`)} dynamicRoutePath={recruitment.company.url}>
        <User
          type="Medium"
          iconPath={recruitment.company.logoImgUrl}
          name={recruitment.company.name}
          description={recruitment.company.overview}
        />
      </Link>
    </StyledRecruitmentCardWrapper>
  )
}

const StyledRecruitmentCardWrapper = styled.div`
  width: 100%;
`
const StyledRecruitmentLinkArea = styled.div`
  width: 100%;
  &:hover {
    .recruitment-img {
      border: solid 1px ${Color.Primary};
    }
  }
`

const StyledThumbnailImageWrapper = styled.div`
  position: relative;
  height: auto;
`

const StyledThumbnailImage = styled.div`
  width: 100%;
  margin-bottom: 12.5px;
  padding-top: 50%;
  border-radius: ${RadiusSize.Medium}px;
  background-size: cover;
  background-position: center center;
  height: auto;
  border: solid 1px ${Color.BorderGray};
  ${Transition.Hover};
  ${({ bgSrc }: StyledProps) => css`
    background-image: url("${bgSrc}");
  `}
`

const StyledLabel = styled(Label)`
  width: 80px;
  position: absolute;
  bottom: 8.5px;
  left: 8px;
`

const StyledFavoriteButton = styled(FavoriteButton)`
  position: absolute;
  bottom: -24px;
  right: 16px;
`

const StyledTagWrapper = styled.div`
  display: flex;
  white-space: nowrap;
  width: calc(100% - 64px);
`

const StyledCategory = styled.div`
  font-size: ${TextSize.XS}px;
  font-weight: bold;
  color: ${Color.Primary};
`

const StyledJobType = styled.div`
  margin-left: 4px;
  font-size: ${TextSize.XS}px;
  font-weight: bold;
  color: ${Color.LightGray};
  overflow: hidden;
  text-overflow: ellipsis;
`

const StyledTitle = styled(Typography)`
  margin: 6px 0 8px;
  height: auto;
  max-height: 44px;
  overflow: hidden;
  font-weight: normal;
`

export default RecruitmentCard
